import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle,
  MessageSquare,
  User,
  Package,
  Calendar,
  Clock
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Button } from '../components/ui';
import { normalizeMessages, formatMessageTypeLabel } from '../utils/offerNormalize';

interface IBidDetails {
  id: string;
  conversationId?: string;
  product: {
    id: string;
    name: string;
    price: number;
    images: string[];
    description?: string;
  };
  buyer: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  seller: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  bidPrice: number;
  amount?: number;
  status: 'PENDING' | 'open' | 'countered' | 'ACCEPTED' | 'REJECTED' | 'expired' | 'EXPIRED' | 'CANCELLED' | 'CONVERTED';
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  adminOverride?: boolean;
  adminReason?: string;
  addToCartLink?: string; // Link for buyer to add accepted bid to cart
}

interface BidMessage {
  id: string;
  sender: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  recipient: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  message: string;
  type: string;
  text?: string;
  offer?: { amount?: number; bidPrice?: number; status?: string };
  createdAt: string;
}

const BidDetails: React.FC = () => {
  const { offerId } = useParams<{ offerId: string }>();
  const { t } = useTranslation('bidding');
  const navigate = useNavigate();
  const [bid, setBid] = useState<IBidDetails | null>(null);
  const [messages, setMessages] = useState<BidMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const fetchBidDetails = useCallback(async () => {
    if (!offerId) return;
    try {
      setLoading(true);
      const response = await api.get(`/admin/offers/${offerId}`);
      const offer = response.data.data;
      const conversationId =
        typeof offer.conversation === 'string'
          ? offer.conversation
          : offer.conversation?.id || offer.conversation?._id;
      setBid({
        ...offer,
        id: offer.id || offer._id,
        bidPrice: offer.bidPrice ?? offer.amount,
        conversationId: conversationId ? String(conversationId) : undefined,
      });
    } catch (error) {
      console.error('Error fetching bid details:', error);
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  const fetchBidMessages = useCallback(
    async (productId: string | undefined, conversationId?: string) => {
      if (!conversationId && !productId) return;
      try {
        setMessagesLoading(true);
        const params = new URLSearchParams();
        if (conversationId) params.set('conversationId', conversationId);
        else if (productId) params.set('productId', productId);
        const response = await api.get(`/admin/offer-messages?${params.toString()}`);
        const payload = response.data.data;
        const raw = payload?.messages ?? (Array.isArray(payload) ? payload : []);
        setMessages(normalizeMessages(raw) as unknown as BidMessage[]);
      } catch (error) {
        console.error('Error fetching bid messages:', error);
      } finally {
        setMessagesLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (offerId) {
      fetchBidDetails();
    }
  }, [offerId, fetchBidDetails]);

  useEffect(() => {
    if (bid?.conversationId || bid?.product?.id) {
      fetchBidMessages(bid.product?.id, bid.conversationId);
    }
  }, [bid?.conversationId, bid?.product?.id, fetchBidMessages]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'countered': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ACCEPTED': return 'bg-green-100 text-green-800 border-green-200';
      case 'REJECTED': return 'bg-red-100 text-red-800 border-red-200';
      case 'CANCELLED': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'expired':
      case 'EXPIRED': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'CONVERTED': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const statusLabel = (status: string) =>
    t(`status.${status}`, { defaultValue: status });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMessageTypeColor = (type: string) => {
    switch (type) {
      case 'BID_PROPOSAL':
      case 'OFFER_CREATED': return 'bg-blue-100 text-blue-800';
      case 'BID_ACCEPTED':
      case 'OFFER_ACCEPTED': return 'bg-green-100 text-green-800';
      case 'BID_REJECTED':
      case 'OFFER_REJECTED':
      case 'OFFER_CANCELLED':
      case 'OFFER_EXPIRED': return 'bg-red-100 text-red-800';
      case 'COUNTER_OFFER': return 'bg-purple-100 text-purple-800';
      case 'SYSTEM': return 'bg-gray-100 text-gray-800';
      case 'PRODUCT_INQUIRY': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('messages.loadingOffer')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!bid) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900">{t('messages.offerNotFound')}</h2>
          <Button
            onClick={() => navigate('/offers')}
            className="mt-4"
          >
            {t('actions.backToOffers')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate('/offers')}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{t('actions.backToOffers')}</span>
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('offerDetails')}</h1>
            <p className="text-gray-600 mt-1">ID: {bid.id}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Bid Overview */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('overview.title')}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">{t('overview.productInfo')}</h3>
                <div className="flex items-start space-x-4">
                  <img
                    src={bid.product.images[0] || '/placeholder-product.png'}
                    alt={bid.product.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div>
                    <h4 className="font-medium text-gray-900">{bid.product.name}</h4>
                    <p className="text-sm text-gray-500">{t('overview.originalPrice')}: {formatCurrency(bid.product.price)}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">{t('overview.offerInfo')}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">{t('fields.offerAmount')}:</span>
                    <span className="text-sm font-semibold text-green-600">
                      {formatCurrency(bid.bidPrice)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">{t('fields.percentOfList')}:</span>
                    <span className="text-sm font-medium">
                      {((bid.bidPrice / bid.product.price) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">{t('fields.status')}:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(bid.status)}`}>
                      {statusLabel(bid.status)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {bid.adminOverride && bid.adminReason && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="text-sm font-medium text-yellow-800 mb-1">{t('overview.adminOverride')}</h4>
                <p className="text-sm text-yellow-700">{bid.adminReason}</p>
              </div>
            )}
          </div>

          {/* User Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('overview.participants')}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">{t('overview.buyer')}</h3>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {bid.buyer.firstName} {bid.buyer.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{bid.buyer.email}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">{t('overview.seller')}</h3>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {bid.seller.firstName} {bid.seller.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{bid.seller.email}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('overview.timeline')}</h2>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('overview.created')}</p>
                  <p className="text-xs text-gray-500">{formatDate(bid.createdAt)}</p>
                </div>
              </div>

              {bid.updatedAt !== bid.createdAt && (
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Clock className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t('overview.updated')}</p>
                    <p className="text-xs text-gray-500">{formatDate(bid.updatedAt)}</p>
                  </div>
                </div>
              )}

              {bid.expiresAt && (
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <Clock className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t('overview.expires')}</p>
                    <p className="text-xs text-gray-500">{formatDate(bid.expiresAt)}</p>
                    {bid.status === 'ACCEPTED' && (
                      <p className="text-xs text-orange-600 mt-1">
                        Buyer has 24 hours to add to cart
                      </p>
                    )}
                  </div>
                </div>
              )}

              {bid.status === 'ACCEPTED' && bid.addToCartLink && (
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Add to Cart Link</p>
                    <p className="text-xs text-gray-500 break-all">{bid.addToCartLink}</p>
                    <p className="text-xs text-green-600 mt-1">
                      Buyer can use this link to add the product to cart with the bid price
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">
              {t('overview.readOnlyHint', {
                defaultValue:
                  'Offers are negotiated between buyer and seller only. Admin can view this thread for support and analytics.',
              })}
            </p>
          </div>

          {/* Messages */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <MessageSquare className="w-5 h-5" />
              <span>{t('overview.relatedMessages')}</span>
            </h3>
            
            {messagesLoading ? (
              <p className="text-sm text-gray-500">{t('messages.loadingMessages')}</p>
            ) : messages.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {messages.map((message) => (
                  <div key={message.id} className="border-l-4 border-blue-200 pl-3 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs px-2 py-1 rounded ${getMessageTypeColor(message.type)}`}>
                        {formatMessageTypeLabel(message.type)}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(message.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{message.text || message.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      From: {message.sender.firstName} {message.sender.lastName}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t('messages.noMessages')}</p>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default BidDetails;